/**
 * My Requests Page
 * MEMBER can view their change requests and track status
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { FiCheckCircle, FiClock, FiXCircle, FiFileText, FiTrash2, FiChevronLeft, FiChevronRight, FiEdit } from 'react-icons/fi';
import { useDialog } from '../hooks/useDialog';
import { toast } from 'react-hot-toast';
import SEO from '../components/common/SEO';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { apiClient } from '../services/api';
import ResubmitRequestModal from '../components/company/ResubmitRequestModal';

interface ChangeRequest {
  id: string;
  type: 'CREATE' | 'UPDATE';
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  requestedData: any;
  userNotes?: string;
  adminNotes?: string;
  createdAt: string;
  reviewedAt?: string;
  companyId?: string;
}

interface PaginationInfo {
  total: number;
  skip: number;
  limit: number;
}

const MyRequestsPage: React.FC = () => {
  const location = useLocation();
  const { showConfirm, ConfirmDialog, AlertDialog } = useDialog();
  const [requests, setRequests] = useState<ChangeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('ALL');
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Resubmit modal states
  const [resubmitModalOpen, setResubmitModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ChangeRequest | null>(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10); // จำนวนรายการต่อหน้า
  const [pagination, setPagination] = useState<PaginationInfo>({
    total: 0,
    skip: 0,
    limit: 10
  });

  // Show success message from location state
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
      // Clear location state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    loadRequests();
  }, [currentPage, filter]); // Reload เมื่อเปลี่ยนหน้าหรือ filter

  const loadRequests = async () => {
    try {
      setLoading(true);

      // Build query parameters
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: pageSize.toString(),
      });

      // Add status filter if not ALL
      if (filter !== 'ALL') {
        params.append('status_filter', filter);
      }

      const response = await apiClient.get(`/api/change-requests/my-requests?${params.toString()}`);
      setRequests(response.data.items || []);
      setPagination({
        total: response.data.total || 0,
        skip: response.data.skip || 0,
        limit: response.data.limit || pageSize
      });
    } catch (error) {
      console.error('Failed to load requests:', error);
      toast.error('ไม่สามารถโหลดข้อมูลคำขอได้');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (requestId: string) => {
    const confirmed = await showConfirm('ยืนยันการลบคำขอ', {
      message: 'คุณต้องการลบคำขอนี้ใช่หรือไม่?',
      confirmText: 'ลบ',
      type: 'danger'
    });

    if (!confirmed) return;

    try {
      await apiClient.delete(`/api/change-requests/${requestId}`);
      toast.success('ลบคำขอสำเร็จ');
      await loadRequests();
    } catch (error) {
      console.error('Failed to delete request:', error);
      toast.error('ไม่สามารถลบคำขอได้');
    }
  };

  const handleResubmit = (request: ChangeRequest) => {
    setSelectedRequest(request);
    setResubmitModalOpen(true);
  };

  const handleResubmitSuccess = () => {
    setResubmitModalOpen(false);
    setSelectedRequest(null);
    loadRequests(); // Reload to show new request
  };

  // Pagination helpers
  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
    setCurrentPage(1); // Reset to page 1 when filter changes
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
            <FiClock className="mr-1" />
            รอการอนุมัติ
          </span>
        );
      case 'APPROVED':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <FiCheckCircle className="mr-1" />
            อนุมัติแล้ว
          </span>
        );
      case 'REJECTED':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <FiXCircle className="mr-1" />
            ปฏิเสธ
          </span>
        );
      default:
        return null;
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'CREATE':
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">เพิ่มบริษัทใหม่</span>;
      case 'UPDATE':
        return <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded">แก้ไขข้อมูล</span>;
      default:
        return null;
    }
  };

  // ไม่ต้อง filter ที่ frontend เพราะ backend filter ให้แล้ว
  // const filteredRequests = requests; // ใช้ requests โดยตรง

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="คำขอของฉัน | Thai Security Directory"
        description="ติดตามสถานะคำขอเพิ่ม/แก้ไขข้อมูลบริษัท"
        url="/my-requests"
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="min-h-screen bg-gray-50 py-8"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">คำขอของฉัน</h1>
            <p className="text-gray-600">ติดตามสถานะคำขอเพิ่ม/แก้ไขข้อมูลบริษัท</p>
          </div>

          {/* Success Message */}
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3"
            >
              <FiCheckCircle className="text-green-600 text-xl" />
              <p className="text-green-800">{successMessage}</p>
            </motion.div>
          )}

          {/* Filters */}
          <div className="mb-6 flex flex-wrap gap-2">
            <Button
              variant={filter === 'ALL' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleFilterChange('ALL')}
            >
              ทั้งหมด ({pagination.total})
            </Button>
            <Button
              variant={filter === 'PENDING' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleFilterChange('PENDING')}
            >
              รอดำเนินการ
            </Button>
            <Button
              variant={filter === 'APPROVED' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleFilterChange('APPROVED')}
            >
              อนุมัติแล้ว
            </Button>
            <Button
              variant={filter === 'REJECTED' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleFilterChange('REJECTED')}
            >
              ปฏิเสธ
            </Button>
          </div>

          {/* Requests List */}
          {requests.length === 0 ? (
            <Card className="p-12 text-center">
              <FiFileText className="mx-auto text-gray-400 text-5xl mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">ไม่มีคำขอ</h3>
              <p className="text-gray-600">
                {filter === 'ALL'
                  ? 'คุณยังไม่มีคำขอเพิ่ม/แก้ไขข้อมูลบริษัท'
                  : `ไม่พบคำขอที่มีสถานะ "${filter}"`
                }
              </p>
            </Card>
          ) : (
            <>
              <div className="space-y-4">
                {requests.map(request => (
                  <Card key={request.id} className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {/* Header */}
                        <div className="flex items-center space-x-3 mb-3">
                          {getTypeBadge(request.type)}
                          {getStatusBadge(request.status)}
                        </div>

                        {/* Company Name */}
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {request.type === 'CREATE'
                            ? request.requestedData.nameTh
                            : `แก้ไขข้อมูล: ${request.requestedData.nameTh || 'บริษัท'}`
                          }
                        </h3>

                        {/* Details */}
                        <div className="text-sm text-gray-600 space-y-1">
                          <p>ส่งคำขอเมื่อ: {new Date(request.createdAt).toLocaleDateString('th-TH', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}</p>
                          {request.reviewedAt && (
                            <p>
                              {request.status === 'APPROVED' ? 'อนุมัติเมื่อ' : 'ปฏิเสธเมื่อ'}: {new Date(request.reviewedAt).toLocaleDateString('th-TH', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                          )}
                          {request.userNotes && (
                            <p className="text-gray-700">
                              <span className="font-medium">หมายเหตุ:</span> {request.userNotes}
                            </p>
                          )}
                          {request.adminNotes && (
                            <p className="text-blue-700 bg-blue-50 p-2 rounded mt-2">
                              <span className="font-medium">ความเห็นจากผู้ดูแล:</span> {request.adminNotes}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center space-x-2">
                        {request.status === 'PENDING' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(request.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <FiTrash2 />
                          </Button>
                        )}
                        {request.status === 'REJECTED' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleResubmit(request)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-300"
                          >
                            <FiEdit className="mr-1" />
                            แก้ไขและส่งใหม่
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-8 flex items-center justify-between border-t border-gray-200 pt-6">
                  {/* Pagination Info */}
                  <div className="text-sm text-gray-600">
                    แสดง {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)} จาก {pagination.total} รายการ
                  </div>

                  {/* Pagination Controls */}
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={!hasPrevPage || loading}
                      className="flex items-center space-x-1"
                    >
                      <FiChevronLeft />
                      <span>ก่อนหน้า</span>
                    </Button>

                    {/* Page Numbers */}
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => {
                          // แสดงเฉพาะหน้าที่อยู่ใกล้หน้าปัจจุบัน
                          return (
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 1 && page <= currentPage + 1)
                          );
                        })
                        .map((page, index, array) => {
                          // แสดง "..." ถ้ามีช่องว่าง
                          if (index > 0 && page - array[index - 1] > 1) {
                            return (
                              <React.Fragment key={`ellipsis-${page}`}>
                                <span className="px-2 text-gray-400">...</span>
                                <Button
                                  key={page}
                                  variant={page === currentPage ? 'default' : 'outline'}
                                  size="sm"
                                  onClick={() => handlePageChange(page)}
                                  disabled={loading}
                                  className="min-w-[2.5rem]"
                                >
                                  {page}
                                </Button>
                              </React.Fragment>
                            );
                          }
                          return (
                            <Button
                              key={page}
                              variant={page === currentPage ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => handlePageChange(page)}
                              disabled={loading}
                              className="min-w-[2.5rem]"
                            >
                              {page}
                            </Button>
                          );
                        })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!hasNextPage || loading}
                      className="flex items-center space-x-1"
                    >
                      <span>ถัดไป</span>
                      <FiChevronRight />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Dialog Components */}
          {ConfirmDialog}
          {AlertDialog}

          {/* Resubmit Request Modal */}
          <ResubmitRequestModal
            isOpen={resubmitModalOpen}
            onClose={() => setResubmitModalOpen(false)}
            request={selectedRequest}
            onSuccess={handleResubmitSuccess}
          />
        </div>
      </motion.div>
    </>
  );
};

export default MyRequestsPage;
